import React, { useState, useMemo, useEffect } from 'react'
import { Typography, Box, Card, CardContent, Avatar } from '@mui/material'
import { Assignment } from '@mui/icons-material'
import { ActivityFilterButtons } from './ActivityFilterButtons'
import { NoActivitiesMessage } from './NoActivitiesMessage'
import { ActivityType } from '../types/activity'
import {
  getActivityColor,
  getActivityIcon,
  formatActivityType,
  extractMembershipStatuses,
  getFullStatusName,
  buildMembershipSequence,
  normalizeActivityItem,
  formatTimestampNZ,
} from '../utils/activityDisplay'
import {
  filterActivitiesByType,
  calculateTypeCounts,
  loadFilterPreferences,
  saveFilterPreferences,
} from '../utils/activityFilters'

// Import ActivityItem from types
import type { ActivityItem } from '../types/activity'

export interface ActivityWithLastLogin {
  activities: ActivityItem[]
  last_login_date?: string | null
  last_login_user_name?: string | null
}

interface OrgActivityFeedProps {
  activityData: ActivityWithLastLogin
  isLoading: boolean
  lastLoginDate?: string | null
  lastLoginUserName?: string | null
}

// shared display helpers are imported from utils/activityDisplay

// buildMembershipSequence imported from utils/activityDisplay

function sortActivities(activities: ActivityItem[]) {
  return activities.sort((a, b) => {
    const aTime = new Date(a.date || a.activity_timestamp)
    const bTime = new Date(b.date || b.activity_timestamp)

    // Special handling for membership changes on the same date
    if (
      a.activity_type === ActivityType.MEMBERSHIP_CHANGE &&
      b.activity_type === ActivityType.MEMBERSHIP_CHANGE &&
      aTime.toDateString() === bTime.toDateString()
    ) {
      // Get all membership changes for this date
      const sameDateMembershipChanges = activities.filter(
        (activity) =>
          activity.activity_type === ActivityType.MEMBERSHIP_CHANGE &&
          new Date(activity.date || activity.activity_timestamp).toDateString() === aTime.toDateString()
      )

      const orderedSequence = buildMembershipSequence(sameDateMembershipChanges)

      // Find positions of a and b in the ordered sequence
      const aIndex = orderedSequence.findIndex(
        (item) => item.activity_details === a.activity_details
      )
      const bIndex = orderedSequence.findIndex(
        (item) => item.activity_details === b.activity_details
      )

      if (aIndex !== -1 && bIndex !== -1) {
        // For recent first display, show most recent first
        return aIndex - bIndex
      }
    }

    // Default sort by timestamp (most recent first)
    return bTime.getTime() - aTime.getTime()
  })
}

const ActivityFeedSingleOrg: React.FC<OrgActivityFeedProps> = ({
  activityData,
  isLoading,
  lastLoginDate,
  lastLoginUserName,
}) => {
  // Filter state
  const [selectedType, setSelectedType] = useState<ActivityType | 'all'>(() =>
    loadFilterPreferences()
  )

  // Map to typed activities
  const rawActivities = (activityData?.activities || []).map(normalizeActivityItem) as ActivityItem[]

  // Calculate type counts before filtering
  const typeCounts = useMemo(() =>
    calculateTypeCounts(rawActivities),
    [rawActivities]
  )

  // Apply filters
  const activities = useMemo(() =>
    filterActivitiesByType(rawActivities, selectedType),
    [rawActivities, selectedType]
  )

  // Handle filter changes
  const handleFilterChange = (type: ActivityType | 'all') => {
    setSelectedType(type)
    saveFilterPreferences(type)
  }

  // Save preferences when they change
  useEffect(() => {
    saveFilterPreferences(selectedType)
  }, [selectedType])

  if (isLoading) {
    return <Typography>Loading activity information...</Typography>
  }

  // Function to reset filters
  const handleResetFilter = () => {
    setSelectedType('all')
    saveFilterPreferences('all')
  }

  if (!activityData?.activities || rawActivities.length === 0) {
    return <Typography>No activity found for this organisation.</Typography>
  }

  // If no activities after filtering, show the message with reset option
  if (activities.length === 0) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Recent Activity
        </Typography>
        <ActivityFilterButtons
          selectedType={selectedType}
          onFilterChange={handleFilterChange}
          typeCounts={typeCounts}
        />
        {lastLoginDate && lastLoginUserName ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Last login:{' '}
            {new Date(
              lastLoginDate + (lastLoginDate.endsWith('Z') ? '' : 'Z')
            ).toLocaleString('en-NZ', {
              timeZone: 'Pacific/Auckland',
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })}{' '}
            - {lastLoginUserName}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Last login: - Unknown
          </Typography>
        )}
        <NoActivitiesMessage
          selectedType={selectedType}
          onReset={handleResetFilter}
          isOrgSpecific={true}
        />
      </Box>
    )
  }

  // SPECIAL CASE: org_created activities with date 2025-05-20 should be the first activity, this is a special case for when orgs created_at field was added
  const orgCreatedActivities = activities.filter(
    (activity) =>
      activity.activity_type === 'org_created' &&
      (activity.date || activity.activity_timestamp).startsWith('2025-05-20')
  )

  const otherActivities = activities.filter(
    (activity) =>
      !(
        activity.activity_type === 'org_created' &&
        (activity.date || activity.activity_timestamp).startsWith('2025-05-20')
      )
  )

  // filter out need_updated activities that have the same timestamp as need_created for the same funding need
  const filteredActivities = otherActivities.filter((activity) => {
    if (activity.activity_type === ActivityType.NEED_UPDATED) {
      // check if there's a need_created activity with the exact same timestamp and title
      const hasMatchingNeedCreated = otherActivities.some(
        (other) =>
          other.activity_type === ActivityType.NEED_CREATED &&
          (other.date || other.activity_timestamp) === (activity.date || activity.activity_timestamp) &&
          other.activity_details ===
            activity.activity_details.replace('updated', 'created')
      )
      return !hasMatchingNeedCreated
    }
    return true
  })

  // custom sort filtered activities for membership changes
  const sortedOtherActivities = sortActivities(filteredActivities)

  // combine other activities first, then special case (org created) activities at bottom
  const sortedActivities = [...sortedOtherActivities, ...orgCreatedActivities]

  // for rendering purposes, to not show date as '2025-05-20'
  const isSpecialCase = (activity: ActivityItem) => {
    return (
      activity.activity_type === 'org_created' &&
      (activity.date || activity.activity_timestamp).startsWith('2025-05-20')
    )
  }


  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Recent Activity
      </Typography>
      <ActivityFilterButtons
        selectedType={selectedType}
        onFilterChange={handleFilterChange}
        typeCounts={typeCounts}
      />
      {lastLoginDate && lastLoginUserName ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Last login:{' '}
          {new Date(
            lastLoginDate + (lastLoginDate.endsWith('Z') ? '' : 'Z')
          ).toLocaleString('en-NZ', {
            timeZone: 'Pacific/Auckland',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}{' '}
          - {lastLoginUserName}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Last login: - Unknown
        </Typography>
      )}
      {sortedActivities.map((activity, index) => (
        <Card
          key={`${activity.org_uuid || 'no-org'}-${activity.date || activity.activity_timestamp}-${index}`}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar
                sx={{
                  bgcolor: getActivityColor(activity.activity_type),
                  width: 32,
                  height: 32,
                  mr: 2,
                }}
              >
                {getActivityIcon(activity.activity_type)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="primary">
                  {formatActivityType(activity.activity_type)}
                </Typography>
                {!isSpecialCase(activity) && (
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestampNZ(activity.date || activity.activity_timestamp)}
                    </Typography>
                  )}
              </Box>
            </Box>
            <Typography variant="body2">{activity.activity_details}</Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  )
}

export default ActivityFeedSingleOrg
